<template>
  <div class="containers">
    <SectionHeader
      :title="$t('containers.containers')"
      @selectProjectUuids="
        projectUuids = $event
        loadData(1)
      "
      :projectSelect="true"
    >
      <template v-slot:meta>
        <div class="button-group">
          <button
            :title="showFilters ? $t('containers.filterCollapse') : $t('containers.filterExpand')"
            @click="showFilters = !showFilters"
            class="button button-grey button-round button-small"
          >
            <span class="visually-hidden">
              {{ showFilters ? $t('containers.filterCollapse') : $t('containers.filterExpand') }}
            </span>
            <span class="material-icons">filter_list</span>
          </button>
          <button @click="downloadCsv" class="button button-green button-small button-round">
            {{ $t('containers.exportContainers') }}
          </button>
        </div>
      </template>
    </SectionHeader>

    <div v-if="showFilters" class="filter-wrap">
      <LoadingSpinner v-if="filtersLoading" narrow />
      <template v-else>
        <div class="search-wrap">
          <SearchField
            :label="$t('meta.search')"
            :name="'container-search-input'"
            :placeholder="$t('containers.searchPlaceholder')"
            v-model="availableFilters.search_phrase"
            @input="searchHandler"
          />
        </div>
        <div class="filters">
          <div v-for="filter in availableFilters.values" :key="filter.name">
            <v-select
              v-show="!filter.hidden"
              v-model="filter.selected"
              :placeholder="filter.name"
              :options="filter.options"
              :multiple="filter.multi"
              @input="loadData(1)"
            />
          </div>
        </div>
      </template>
    </div>

    <div class="content">
      <div class="content-area container-list">
        <LoadingSpinner v-if="containersLoading" />
        <p v-else-if="containers.length === 0" class="alert alert-info">{{ $t('search.noResults') }}.</p>
        <div v-else v-for="container in containers" :key="container.uuid">
          <div class="area-wrap">
            <span v-if="'fill_level' in container" class="fill-level">
              <span :class="getFillLevelColorClass(container.measured_at, container.fill_level)">
                {{ container.fill_level }}%
              </span>
              <span class="material-icons icon-right" :class="olderThan7Days(container.measured_at) && 'fl-neutral'"
                >sensors</span
              >
            </span>
            <span v-else-if="!container.full_report_navixy" class="fill-level">
              <span
                v-show="container.last_route_status === 'skipped'"
                class="fl-neutral"
                :title="$t('containers.notEmptied')"
              >
                {{ $t('containers.notEmptiedAbbr') }}
              </span></span
            >
            <a @click="jumpOnMap(container)" class="jump-on-map">{{ $t('containers.showOnMap') }}</a>
            <h3>
              <Link :to="'/containers/' + container.uuid">{{
                container.lent_to_company ? `${container.name} (${$t('meta.external')})` : container.name
              }}</Link>
            </h3>
            <p v-if="container.preformatted">
              <span class="material-icons">gps_fixed</span><span>{{ container.preformatted }}</span>
            </p>
            <p v-if="container.fraction">
              <span class="material-icons">recycling</span><span>{{ container.fraction.localized_name }}</span>
            </p>
            <p v-if="container.measured_at_formatted">
              <span class="material-icons">schedule</span><span>{{ container.measured_at_formatted }}</span>
            </p>
            <p v-if="container.type_volume">
              <span class="material-icons">restore_from_trash</span
              ><span>{{ $t('meta.volume') }}: {{ container.type_volume }} m³</span>
            </p>
            <p v-if="container.sensors && container.sensors.length > 0">
              <span class="material-icons">sensors</span><span>{{ container.sensors[0].hardware_id }}</span>
            </p>
            <p v-if="container.empty_templates && container.empty_templates.length > 0">
              <span class="material-icons">event</span>
              <span>
                <template v-for="(empty_template, i) of container.empty_templates">
                  <template v-if="empty_template.repeat_type === 'once'">{{ $t('containers.once') }}</template>
                  <template v-else
                    >{{
                      empty_template.repeat_interval === 1
                        ? $t('containers.everyWeek')
                        : $t('containers.everyWeekInterval', { num: empty_template.repeat_interval })
                    }}
                    {{ weekday(empty_template.reference_date)
                    }}{{ i < container.empty_templates.length - 1 ? ',' : '' }}
                  </template>
                </template>
              </span>
            </p>
            <p v-if="container.uvv_checks && container.uvv_checks.length > 0">
              <span class="material-icons">event_available</span
              ><span>{{ $t('containers.uvvCheckDueAt') }}: {{ container.uvv_checks[0].date_formatted }}</span>
            </p>
            <p v-if="container.zone_name || container.area_name || container.site_name">
              <span class="material-icons">pin_drop</span>
              <span>{{
                [container.zone_name, container.area_name, container.site_name].filter(Boolean).join(', ')
              }}</span>
            </p>
            <p v-if="container.internal_id">
              <span class="material-icons">fingerprint</span><span>{{ container.internal_id }}</span>
            </p>
            <p v-if="container.project">
              <span class="material-icons">all_inbox</span><span>{{ container.project.name }}</span>
            </p>
            <p
              v-if="
                container.full_report_navixy &&
                container.sensors &&
                container.sensors.length > 0 &&
                container.sensors[0].sensor_type == 'navixy'
              "
            >
              <span class="material-icons"><FullReportSvg /></span>
              <span>{{ $tc('sensors.navixy.fullReport', container.full_report_navixy) }}</span>
            </p>
          </div>
        </div>
        <div class="pagination" v-if="!containersLoading && pagination && pagination.totalObjects">
          <pagination
            :records="pagination.totalObjects"
            v-model="pagination.page"
            :per-page="25"
            @paginate="loadData"
            :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
          />
        </div>
      </div>
      <div class="map-container">
        <div v-show="!cookiesAccepted" class="map-placeholder map">
          <h3>{{ $t('meta.acceptCookies') }}</h3>
          <p>{{ $t('meta.acceptCookiesDesc') }}</p>
          <p class="cta">
            <a @click="acceptCookies" class="button button-green button-small button-round">
              {{ $t('meta.acceptCookiesCta') }}
            </a>
          </p>
        </div>
        <div v-show="cookiesAccepted" id="map" class="map" />
      </div>
    </div>
  </div>
</template>

<script>
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import bbox from '@turf/bbox'
import { debounce } from 'lodash'
import mapbox from 'mapbox-gl'
import spiderifier from 'mapboxgl-spiderifier'
import Vue from 'vue'
import Pagination from 'vue-pagination-2'

import SearchField from '@/components/Form/Search/SearchField.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import config from '@/components/config'
import MapPin from '@/components/container/MapPin.vue'
import MapPopup from '@/components/container/MapPopup.vue'
import { mapboxFeatureTypes, mapboxGeometryTypes } from '@/components/mixins/constants'
import dateUtils from '@/components/mixins/dateUtils'
import FullReportSvg from '@/components/svg/FullReportSvg.vue'

import containersApi from '@/api/container'
import containerFiltersApi from '@/api/containerFilters'
import exportsApi from '@/api/exports'
import sitesApi from '@/api/sites'
import zonesApi from '@/api/zones'
import mapUtils from '@/utils/mapbox'
import { SEARCH_INPUT_DELAY } from '@/utils/searchInputDelay'
import { mapSettings } from '@/utils/settings'

let MapPopupClass = Vue.extend(MapPopup)
let MapPinClass = Vue.extend(MapPin)

export default {
  name: 'all_containers',
  inject: ['companyUser'],
  props: {
    searchInputDelay: {
      type: Number,
      default: SEARCH_INPUT_DELAY,
    },
  },
  components: {
    FullReportSvg,
    LoadingSpinner,
    Pagination,
    SearchField,
    SectionHeader,
  },
  data: () => ({
    baseUrl: '',
    containers: [],
    containersLoading: true,
    containersGeojsonBranch: { type: 'FeatureCollection', features: [] },
    containersGeojsonIndustry: { type: 'FeatureCollection', features: [] },
    searchPhrase: '',
    map: null,
    popup: '',
    pagination: { page: 1, totalObjects: 0 },
    projectUuids: [],
    sites: [],
    filtersLoading: true,
    availableFilters: {},
    showFilters: true,
    zones: [],
  }),
  computed: {
    ...mapSettings(['cookiesAccepted', 'userLanguage']),
    searchHandler() {
      return debounce(
        () => this.loadData(1),
        this.searchInputDelay, // Limit search API calls to once per 400ms while typing
        { trailing: true },
      )
    },
    industryPointPaint() {
      return {
        'circle-color': config.MAPBOX_BOX_COLORS.ZONE,
        'circle-radius': config.MAPBOX_CLUSTER_CIRCLE_RADIUS,
        'circle-stroke-width': config.MAPBOX_BOX_BORDER_WIDTH.CLUSTER_STROKE,
        'circle-stroke-opacity': config.MAPBOX_BOX_OPACITY.CLUSTER_STROKE,
        'circle-stroke-color': config.MAPBOX_BOX_COLORS.BORDER,
        'circle-opacity': config.MAPBOX_BOX_OPACITY.ZONE,
      }
    },
  },
  mixins: [mapUtils, dateUtils],
  methods: {
    parseContainerFilters() {
      let filters = { values: [] }
      if (this.availableFilters.search_phrase !== '') {
        filters.search_phrase = this.availableFilters.search_phrase
      }
      filters.values = this.availableFilters.values.flatMap(f =>
        f.selected !== '' ? [{ name: f.name, selected: f.selected }] : [],
      )
      return filters
    },
    async loadData(page = 1) {
      page = page || this.pagination.page

      if (this.filtersLoading) return

      await this.loadContainers(page)
      await this.loadSites()
      await this.loadZones()
      await this.loadContainerGeojson()
    },
    async loadContainers(page) {
      this.containersLoading = true
      const response = await containersApi.getAll({
        page,
        lang: this.userLanguage,
        filters: this.parseContainerFilters(),
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
      })

      this.containers = response.containers
      this.pagination.page = page
      this.pagination.totalObjects = response.pagination?.total_objects
      this.containersLoading = false
    },
    async loadContainerFilters() {
      this.filtersLoading = true
      this.availableFilters = await containerFiltersApi.getAll(this.companyUser.company_uuid)
      this.filtersLoading = false
    },
    async loadSites() {
      this.sites = await sitesApi.getAllForCompany(this.companyUser.company_uuid)
    },
    async loadZones() {
      for (const site of this.sites) {
        const zones = await zonesApi.getAllInSite(site.uuid)
        if (zones?.length) {
          this.zones = [...this.zones, ...zones]
        }
      }
    },
    async loadContainerGeojson() {
      const result = await containersApi.getGeoJson({
        filters: this.parseContainerFilters(),
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
      })
      const [geoBranch, geoIndustry] = result?.features
        ? result.features.reduce(
            (result, feature) => {
              result[!feature.properties.zone_uuid ? 0 : 1].push(feature)
              return result
            },
            [[], []],
          )
        : [[], []]

      this.containersGeojsonBranch.features = geoBranch.map(feature => {
        feature.geometry.coordinates = feature.geometry.coordinates.map(c => parseFloat(c))
        feature.properties.old_fill_level = this.olderThan7Days(feature.properties.measured_at)
        return feature
      })
      this.containersGeojsonIndustry.features = geoIndustry.map(feature => {
        const zone = this.zones.find(z => feature.properties.zone_uuid === z.uuid)

        if (zone === undefined || !zone.mb_feature_polygon) {
          return false
        }

        const featurePolygon = JSON.parse(zone.mb_feature_polygon)
        // Calculate bounding box
        const bounds = bbox(featurePolygon)
        // Calculate center of bounding box to have the center
        // of the polygon of the zone where the container is at
        const centerPoint = mapUtils.calculateCenterOfCoordinates([
          { lng: bounds[0], lat: bounds[1] },
          { lng: bounds[2], lat: bounds[3] },
        ])
        return {
          type: mapboxFeatureTypes.FEATURE,
          geometry: {
            type: mapboxGeometryTypes.POINT,
            coordinates: [centerPoint.lng, centerPoint.lat],
          },
        }
      })

      setTimeout(() => this.initMap(), 200)
    },
    initMap() {
      if (!this.cookiesAccepted) return
      const firstBranchCoords = this.containersGeojsonBranch.features[0]?.geometry?.coordinates
      const coordinates = firstBranchCoords
        ? firstBranchCoords.map(parseFloat)
        : [this.containers[0]?.lng, this.containers[0]?.lat]

      this.baseUrl = process.env.VUE_APP_BASE_APP

      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true,
        },
      })

      const siteBoxes = this.sites.map(site => mapUtils.siteToBox(site))

      const areas = this.sites.flatMap(site => site.areas)
      const areaBoxes = areas.map(area => mapUtils.areaToBox(area))

      const zoneBoxes = this.zones.map(zone => mapUtils.zoneToBox(zone))

      const sitesFeatureCollection = {
        type: 'FeatureCollection',
        features: this.sites.flatMap(site => JSON.parse(site.mb_feature_polygon).features),
      }

      this.map = mapUtils.initMap({
        container: 'map',
        center: { lat: coordinates[1], lng: coordinates[0] },
        zoom: 9,
        enableFullscreen: true,
        enableNavigationControls: true,
        controls: [draw],
        boxes: [...siteBoxes, ...areaBoxes, ...zoneBoxes],
        fitBounds: sitesFeatureCollection,
      })

      this.map.on('load', () => {
        const pinClickFunction = (e, fillLevelColor, spiderLeg) => {
          if (this.popup) this.popup.remove()
          this.popup = this.createPopup(spiderLeg.param.x, spiderLeg.param.y, fillLevelColor, spiderLeg.feature).addTo(
            this.map,
          )

          spiderLeg.mapboxMarker.setPopup(this.popup)
          e.stopPropagation()
        }
        const mapSpiderifier = this.initSpiderifier(pinClickFunction)
        // loads the green, red, yellow containers dialog bubbles
        this.loadMapIconImages()

        this.map.on('click', 'clusters', e => {
          const features = this.map.queryRenderedFeatures(e.point, {
            layers: ['clusters'],
          })
          const clusterId = features[0].properties.cluster_id
          this.map.getSource('containers').getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return

            this.map.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom,
            })
          })
        })

        this.map.on('click', 'unclustered-point', e => {
          const featureCount = this.map.queryRenderedFeatures(e.point, {
            layers: ['unclustered-point'],
          })
          if (featureCount?.length >= 2) {
            let features = []
            featureCount.forEach((el, index) => {
              features.push({
                coordinates: el.geometry.coordinates.slice(),
                fill_level: el.properties.fill_level,
                id: index,
                location: el.properties.location,
                measured_at: el.properties.measured_at,
                measured_at_formatted: el.properties.measured_at_formatted,
                name: el.properties.name,
                uuid: el.properties.uuid,
              })
            })
            mapSpiderifier.spiderfy(featureCount[0].geometry.coordinates, features)
          } else if (featureCount?.length === 1) {
            const coords = featureCount[0].geometry.coordinates.slice()
            const pinHtml = this.pinColorByFillLevel(
              featureCount[0].properties.measured_at,
              featureCount[0].properties.fill_level,
            )
            this.popup = this.createPopup(0, -10, pinHtml, featureCount[0].properties)
            this.popup.setLngLat(coords).addTo(this.map)
          } else {
            mapSpiderifier.unspiderfy()
            if (this.popup) {
              this.popup.remove()
            }
            return
          }
        })
      })
    },
    createPopup(offsetX, offsetY, fillLevelColor, feature) {
      const popup = new mapbox.Popup({
        closeButton: true,
        closeOnClick: true,
        offset: [offsetX, offsetY],
      })

      const MapPopupInstance = new MapPopupClass({
        propsData: {
          fillLevelColor: fillLevelColor,
          feature: feature,
          showDetails: this.$t('containers.showDetails'),
        },
      })
      MapPopupInstance.$mount()
      return popup.setHTML(MapPopupInstance.$el.outerHTML)
    },
    // When zoomed out, containers are gathered into blue points (clusters) with count
    loadContainerClusterPoints() {
      this.map.addSource('containers', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: this.containersGeojsonBranch.features,
        },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      })
      this.map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'containers',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#314053',
          'circle-radius': 18,
          'circle-stroke-width': 5,
          'circle-stroke-opacity': 0.5,
          'circle-stroke-color': '#314053',
        },
      })
      this.map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'containers',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': 14,
        },
        paint: {
          'text-color': 'white',
        },
      })
      this.map.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: 'containers',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': [
            'case',
            ['>=', ['to-number', ['get', 'count']], 2],
            'icon-multi',
            [
              'any',
              ['!', ['to-boolean', ['get', 'fill_level']]],
              ['boolean', ['get', 'old_fill_level']],
              ['all', ['boolean', ['has', 'fill_level'], true], ['<=', ['get', 'fill_level'], 0]],
            ],
            'icon-pin',
            ['all', ['boolean', ['has', 'fill_level'], true], ['>', ['get', 'fill_level'], 0]],
            ['step', ['get', 'fill_level'], 'icon-green', 50, 'icon-yellow', 75, 'icon-red'],
            'icon-multi',
          ],
          'icon-size': [
            'case',
            [
              'all',
              ['<', ['to-number', ['get', 'count']], 2],
              [
                'any',
                ['!', ['to-boolean', ['get', 'fill_level']]],
                ['<=', ['get', 'fill_level'], 0],
                ['boolean', ['get', 'old_fill_level']],
              ],
            ],
            0.25,
            1,
          ],
          'text-field': [
            'case',
            [
              'any',
              ['>=', ['to-number', ['get', 'count']], 2],
              ['!', ['to-boolean', ['get', 'fill_level']]],
              ['<=', ['get', 'fill_level'], 0],
              ['boolean', ['get', 'old_fill_level']],
            ],
            '',
            ['all', ['boolean', ['has', 'fill_level'], true], ['>', ['get', 'fill_level'], 0]],
            ['concat', ['to-string', ['get', 'fill_level']], '%'],
            'error',
          ],
          'text-anchor': 'bottom',
          'text-offset': [0, 0.35],
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'icon-allow-overlap': true,
          'text-allow-overlap': true,
          'icon-ignore-placement': true,
        },
        paint: {
          'text-color': '#fff',
        },
      })
    },
    loadIndustryContainerClusterPoints() {
      this.map.addSource('industryContainers', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: this.containersGeojsonIndustry.features,
        },
        cluster: true,
        clusterMaxZoom: 30,
        clusterRadius: 50,
      })
      this.map.addLayer({
        id: 'industry-clusters',
        type: 'circle',
        source: 'industryContainers',
        filter: ['has', 'point_count'],
        paint: this.industryPointPaint,
      })
      this.map.addLayer({
        id: 'industry-cluster-count',
        type: 'symbol',
        source: 'industryContainers',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': 14,
        },
        paint: {
          'text-color': 'white',
        },
      })
      this.map.addLayer({
        id: 'unclustered-industry-point',
        type: 'circle',
        source: 'industryContainers',
        filter: ['!', ['has', 'point_count']],
        paint: this.industryPointPaint,
      })
      this.map.addLayer({
        id: 'unclustered-industry-point-symbol',
        type: 'symbol',
        source: 'industryContainers',
        filter: ['!', ['has', 'point_count']],
        layout: {
          // Always put 1, as this is an unclustered point
          'text-field': '1',
          'text-font': config.MAPBOX_CLUSTER_FONT,
          'text-size': config.MAPBOX_CLUSTER_TEXT_SIZE,
        },
        paint: {
          'text-color': config.MAPBOX_BOX_COLORS.TEXT,
        },
      })
    },
    async loadMapIconImages() {
      await Promise.all(
        config.MAPBOX_ICONS.map(
          ({ name, icon }) =>
            new Promise(resolve => {
              this.map.loadImage(icon, (error, image) => {
                if (error) throw error
                this.map.addImage(name, image)
                resolve()
              })
            }),
        ),
      )

      // Since mapbox-gl's loadImage() is an async operation it is good practice to wait for it
      // to finish before adding sources and layers which will use the loaded images to represent data.
      // See example: https://docs.mapbox.com/mapbox-gl-js/example/add-image/
      this.loadContainerClusterPoints()
      this.loadIndustryContainerClusterPoints()
    },
    pinColorByFillLevel(date, fillLevel) {
      if (this.olderThan7Days(date)) return 'level-invalid'

      if (fillLevel < 50) return 'level-green'
      else if (fillLevel < 76) return 'level-yellow'
      else if (fillLevel > 75) return 'level-red'
      return ''
    },
    initSpiderifier(pinClickFunction) {
      const initializeSpiderLeg = spiderLeg => {
        const MapPinInstance = new MapPinClass({
          propsData: {
            spiderLeg: spiderLeg,
            pinClick: pinClickFunction,
          },
        })
        MapPinInstance.$mount()
        spiderLeg.elements.pin.append(MapPinInstance.$el)
      }

      return new spiderifier(this.map, {
        animate: true,
        animationSpeed: 200,
        customPin: true,
        initializeLeg: initializeSpiderLeg,
      })
    },
    jumpOnMap(container) {
      const zone = container.zone_uuid ? this.zones.find(zone => zone.uuid === container.zone_uuid) : null

      if (zone && zone.mb_feature_polygon) {
        const zoneBoundingBox = bbox(JSON.parse(zone.mb_feature_polygon))
        this.map.fitBounds(zoneBoundingBox, {
          padding: config.MAPBOX_FIT_BOUNDS_PADDING,
        })
      } else {
        this.map.flyTo({
          center: [container.lng, container.lat],
          essential: true,
          zoom: config.MAP_DEFAULT_ZOOM,
        })
      }
    },
    async downloadCsv() {
      await exportsApi.create({ type: 'containers_csv', company_uuid: this.companyUser.company_uuid })
    },
    acceptCookies() {
      this.cookiesAccepted = !this.cookiesAccepted
      setTimeout(() => {
        this.initMap()
      }, 200)
    },
    getFillLevelColorClass(date, fillLevel) {
      if (this.olderThan7Days(date)) {
        return 'fl-neutral'
      } else {
        if (fillLevel < 50) {
          return 'fl-g'
        } else if (fillLevel <= 75) {
          return 'fl-o'
        } else if (fillLevel > 75) {
          return 'fl-r'
        }
      }
    },
  },
  async mounted() {
    await this.loadContainerFilters()
  },
}
</script>

<style lang="scss" scoped>
.containers {
  height: calc(100vh - var(--section-header-height));
  display: flex;
  flex-direction: column;
}

.content {
  display: flex;
  flex-direction: row;
  height: 100%;
  overflow: hidden;
}

.map-container {
  flex-grow: 1;
  padding: 10px;
  display: flex;
  justify-content: center;
}

.map {
  flex: 1;
}

.container-list {
  z-index: 0;
  overflow-y: scroll;
  height: 100%;
}

.map {
  margin-bottom: 0;
}

.filter-wrap {
  background: #f9f9f9;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 10px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 10px;

  .search-wrap {
    width: 485px;
  }

  .filters {
    width: calc(1400px - 500px);
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    > * {
      flex: 33.3% 0;

      .v-select {
        margin-right: 10px;
        margin-bottom: 10px;
        background: #fff;
      }
    }
  }
}

.material-icons {
  &.icon-right {
    display: block;
    text-align: right;
    margin-top: 3px;
  }
}

.button-group {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 0.75rem;

  & .button {
    margin: 0 !important;
  }

  .material-icons {
    font-size: inherit;
    vertical-align: middle;
  }
}
</style>
